import "../styles/challenge.css";
import type { ChallengeCodes, LevelCatalog } from "@highmarch/app";
import type { Challenge, ChallengeRead, ChallengeRecord } from "@highmarch/domain";
import { bindSeg, btnHtml, segHtml } from "../kit";
import { icon } from "../iconsKit";
import { rowHtml, sheetHeadHtml } from "../components";
import { evBannerHtml, evChipHtml, evKickHtml } from "../hub/evKit";
import { SHOP_SKIN } from "../shopSkin";
import { paintedIconHtml } from "../featureIcons";
import { fmtClear, rulesChipsHtml } from "./challengeWords";
import { bindCreateTab, createTabHtml, CreateState } from "./challengeCreate";

/** What the Challenge codes sheet needs. */
export interface ChallengeSheetCtx {
  readonly catalog: LevelCatalog;
  readonly codes: ChallengeCodes;
  readonly now: () => number;
  /** Starts a code's battle (the sheet goes first). */
  readonly start: (c: Challenge) => void;
  /** Hands a code to the share sheet, else the clipboard (null: neither here, SHARE is left out). */
  readonly share: ((code: string, c: Challenge) => void) | null;
  /** Reads the clipboard's text (null: no clipboard to read here, PASTE is left out). */
  readonly paste: (() => Promise<string | null>) | null;
}

export type ChallengeTab = "enter" | "create" | "recent";
const TABS: readonly { key: ChallengeTab; label: string }[] = [{ key: "enter", label: "ENTER" }, { key: "create", label: "CREATE" }, { key: "recent", label: "RECENT" }];
/** The banner's words per tab. */
const BANNER_TITLE: Readonly<Record<ChallengeTab, string>> = { enter: "Duel a friend", create: "Make a duel", recent: "Your duels" };
/** The sheet head's painted portrait and a played code's row lead (CSS px), as Daily Road's sheet. */
const HEAD_ICON_PX = 52, ROW_ICON_PX = 30;

/** A code's best as a short line: "Won in 3:07" / "Reached wave 6". */
export function bestWords(r: ChallengeRecord | null): string {
  return r === null ? "Not played yet" : r.won ? `Your best: won in ${fmtClear(r.ms)}` : `Your best: wave ${r.stages}`;
}

function previewHtml(ctx: ChallengeSheetCtx, r: ChallengeRead | null): string {
  if (r === null) return "";
  if (!r.ok) return `<p class="chmsg bad">${icon("close", 14)}<span>${r.message}</span></p>`;
  const c = r.challenge, L = ctx.catalog.balance.level(c.level), best = ctx.codes.recent().find((x) => x.code === r.code) ?? null;
  return `<section class="chcard"><div class="chcardt"><small class="disp">${r.code}</small><b class="disp">Level ${c.level} · ${L.name}</b><span>${bestWords(best)}</span></div>${rulesChipsHtml(ctx.catalog, c)}</section>`;
}

function enterHtml(ctx: ChallengeSheetCtx, text: string, r: ChallengeRead | null): string {
  return `<div class="chentry"><input class="chinput" type="text" inputmode="text" autocapitalize="characters" autocomplete="off" autocorrect="off" spellcheck="false" maxlength="64"
      placeholder="HM1-XXXX-XXXX-XXXX-XXXX" aria-label="Challenge code" value="${text.replace(/[^0-9A-Za-z\s-]/g, "")}">${ctx.paste ? btnHtml("PASTE", "blue", { className: "disp chpaste" }) : ""}</div>
    <div class="chpreview">${previewHtml(ctx, r)}</div>
    ${btnHtml("PLAY", "gold", { icon: "play", className: "disp go chplay", disabled: !r?.ok })}`;
}

function recentHtml(ctx: ChallengeSheetCtx): string {
  const list = ctx.codes.recent();
  if (list.length === 0) return `<p class="chmsg">${icon("flag", 14)}<span>No duels yet</span></p>`;
  const rows = list.map((r) => rowHtml({
    lead: paintedIconHtml("challenge", ROW_ICON_PX), title: `<span class="chcodetxt">${r.code}</span>`, tap: true, compact: true, className: `chrow${r.won ? " won" : ""}`, data: `data-code="${r.code}"`,
    sub: `Level ${r.level} · ${r.runs} battle${r.runs === 1 ? "" : "s"}`, end: `${r.won ? `<b class="chtime disp">${fmtClear(r.ms)}</b>` : `<span class="chwave">wave ${r.stages}</span>`}${icon("chev", 16)}`,
  })).join("");
  return `${evKickHtml("YOUR DUELS", `${list.length}`)}<div class="chlist">${rows}</div>`;
}

/**
 * Events → Challenge codes (MP-28, docs/MODES.md §7), beside Daily Road: ENTER a code (typed or pasted; a bad one is
 * refused with its reason, a good one shows its rules and your best, PLAY starts it), CREATE one from a cleared level
 * (seed, towers, up to two twists, a Fortress HP cap; SHARE and PLAY), and RECENT — the codes played with their bests.
 */
export function showChallengeSheet(parent: HTMLElement, ctx: ChallengeSheetCtx, tab: ChallengeTab = "enter", code = ""): HTMLElement {
  parent.querySelector(":scope > .sheet.challenge")?.remove();
  const sheet = document.createElement("div");
  sheet.className = `sheet panel challenge ${SHOP_SKIN}`;
  parent.append(sheet);
  let current = tab, text = code, shownTab: ChallengeTab | null = null;
  let create: CreateState = { draft: ctx.codes.draft(ctx.now()) };
  const read = (): ChallengeRead | null => (text.trim() === "" ? null : ctx.codes.read(text));
  const render = (): void => {
    const keep = current === shownTab ? (sheet.querySelector<HTMLElement>(".chbody")?.scrollTop ?? 0) : 0;
    shownTab = current;
    const body = current === "enter" ? enterHtml(ctx, text, read()) : current === "create" ? createTabHtml(ctx, create) : recentHtml(ctx);
    sheet.innerHTML = `${sheetHeadHtml({ title: "Challenge codes", portrait: paintedIconHtml("challenge", HEAD_ICON_PX), tint: "ember" })}
      ${evBannerHtml({ scene: "evchallenge", title: BANNER_TITLE[current], chips: evChipHtml(`${icon("star", 13)}For glory`) })}
      ${segHtml(TABS, current, "Challenge codes")}<div class="chbody ${current}">${body}</div>`;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    bindSeg(sheet, (k) => { current = k as ChallengeTab; render(); });
    if (current === "enter") bindEnter();
    else if (current === "create") bindCreateTab(sheet, ctx, create, (s) => { create = s; render(); }, () => sheet.remove());
    else for (const b of sheet.querySelectorAll<HTMLButtonElement>(".chrow")) b.onclick = () => { text = b.dataset.code ?? ""; current = "enter"; render(); };
    sheet.querySelector<HTMLElement>(".chbody")!.scrollTop = keep;   // a pick on Create redraws in place
  };
  const bindEnter = (): void => {
    const input = sheet.querySelector<HTMLInputElement>(".chinput")!, play = sheet.querySelector<HTMLButtonElement>(".chplay")!;
    const refresh = (): void => {
      const r = read();
      sheet.querySelector(".chpreview")!.innerHTML = previewHtml(ctx, r);
      play.disabled = !r?.ok;
    };
    input.oninput = () => { text = input.value; refresh(); };
    play.onclick = () => { const r = read(); if (r?.ok) { sheet.remove(); ctx.start(r.challenge); } };
    sheet.querySelector<HTMLButtonElement>(".chpaste")?.addEventListener("click", () => {
      void ctx.paste?.().then((t) => {
        if (t === null) { input.focus(); return; }
        text = t; input.value = t; refresh();
      });
    });
  };
  render();
  return sheet;
}
